import { useCallback } from 'react';
import { DiGit } from 'react-icons/di';
import { FaCode, FaFlag, FaRegCommentDots } from 'react-icons/fa';
import { MdAccessTimeFilled, MdVisibilityOff } from 'react-icons/md';
import { SiJira, SiSlack } from 'react-icons/si';
import { SlMagicWand } from 'react-icons/sl';
import { useSnapshot } from 'valtio';
import { ReactComponent as ExclusionsIcon } from '../../../assets/icons/exclusions-small.svg';
import { ReactComponent as PolicyEdit } from '../../../assets/icons/policy-edit.svg';
import { ReactComponent as PolicyXIcon } from '../../../assets/icons/policyX.svg';
import { isDevelopment } from 'env-utils';
import { AppPages } from '../../../common/routing/app-navigator';
import { setMenuIssueId } from '../../active-issues/actions/active-issues-actions';
import {
  setApplicationIrrelevantModal,
  setCommentModal,
  setFilesDiffModalVisibility,
  setPolicyDisableModal,
  setPolicyFixModal,
  setQuickFixModalVisibility,
} from '../../active-issues/store-actions/issue-store-actions';
import IssuesStore from '../../active-issues/stores/issues-store';
import { openCreateJiraTicketDialog } from '../actions/create-jira-ticket-modal-actions';
import { useIssuesExclusions } from '../actions/issues-exclusions-actions';
import {
  setExcludeIssueModal,
  setIsExcludeDedault,
} from '../store-actions/issues-exclusions-store-actions';
import { IssueAction } from '../types/issues-types';
import { openSendSlackMessageDialog } from '../actions/slack-actions';

const useIssuesActions = (): IssueAction[][] => {
  const isDev = isDevelopment();
  const { selectedIssue, isActiveJiraConnection, isActiveSlackConnection } =
    useSnapshot(IssuesStore);
  const { selectedAggs } = useIssuesExclusions();
  const isPRAvailable = Boolean(selectedIssue?.isPRAvailable);

  const openSlackModal = useCallback((): void => {
    openSendSlackMessageDialog();
    setMenuIssueId(null);
  }, []);

  const openPRModal = useCallback((): void => {
    if (!selectedAggs?.length) return;
    setQuickFixModalVisibility(true);
    setFilesDiffModalVisibility(false);
  }, [selectedAggs?.length]);

  const onViewCodeFixClick = useCallback((): void => {
    if (!selectedAggs?.length) return;
    setQuickFixModalVisibility(false);
    setFilesDiffModalVisibility(true);
  }, [selectedAggs?.length]);

  const handleExcludeIssue = () => {
    setIsExcludeDedault(true);
    setExcludeIssueModal(true);
  };

  const openPolicyFix = () => {
    setPolicyFixModal(true);
  };

  const handleApplicationIrrelevant = useCallback(() => {
    setApplicationIrrelevantModal(true);
    setMenuIssueId(null);
  }, []);

  const handleAddComment = useCallback(() => {
    setCommentModal(true);
  }, []);

  const handleCreateJiraTicket = useCallback(() => {
    openCreateJiraTicketDialog();
    setMenuIssueId(null);
  }, []);

  const handleDisablePolicy = useCallback(() => {
    setPolicyDisableModal(true);
    setMenuIssueId(null);
  }, []);

  const openPolicyConfig = useCallback(() => {
    const win = window.open(
      `${window.location.origin}${AppPages.Policies}?categoryId=${selectedIssue?.category.categoryId}&policyId=${selectedIssue?.policy.id}`,
      '_blank',
    );
    if (win != null) {
      win.focus();
    }
    setMenuIssueId(null);
  }, [selectedIssue?.category.categoryId, selectedIssue?.policy.id]);

  return [
    [
      {
        text: 'Add A Comment',
        icon: FaRegCommentDots,
        onClick: handleAddComment,
        highlight: false,
      },
      ...(isDev
        ? [
            {
              text: selectedIssue?.fixes
                ? selectedIssue.fixes.tooltip
                : 'Fix Issue',
              icon: SlMagicWand,
              onClick: openPolicyFix,
              disabled: !selectedIssue?.fixes,
              highlight: !!selectedIssue?.fixes,
            },
          ]
        : []),
      ...(isDev
        ? [
            {
              text: 'View Code Fix',
              icon: FaCode,
              onClick: onViewCodeFixClick,
              disabled: !isPRAvailable,
              highlight: isPRAvailable,
            },
          ]
        : []),
    ],
    [
      ...(isDev
        ? [
            {
              text: 'Open PR',
              icon: DiGit,
              onClick: openPRModal,
              disabled: !isPRAvailable,
              highlight: isPRAvailable,
            },
            {
              text: isActiveSlackConnection
                ? 'Send to Slack'
                : 'Slack is not connected',
              icon: SiSlack,
              onClick: openSlackModal,
              disabled: !isActiveSlackConnection,
              highlight: false,
            },
          ]
        : []),

      {
        text: isActiveJiraConnection
          ? 'Create JIRA Ticket'
          : 'Jira is not connected',
        icon: SiJira,
        onClick: handleCreateJiraTicket,
        disabled: !isActiveJiraConnection,
        highlight: false,
      },
    ],
    [
      {
        text: 'Exclude Issue',
        icon: ExclusionsIcon,
        onClick: handleExcludeIssue,
        highlight: false,
      },
      {
        text: 'Snooze Issue',
        icon: MdAccessTimeFilled,
        onClick: handleExcludeIssue,
        highlight: false,
      },
      {
        text: 'Make App Irrelevant',
        icon: MdVisibilityOff,
        onClick: handleApplicationIrrelevant,
        highlight: false,
      },
      {
        text: 'Disable Policy',
        icon: PolicyXIcon,
        onClick: handleDisablePolicy,
        highlight: false,
      },
      {
        text: 'View/Edit Policy',
        icon: PolicyEdit,
        onClick: openPolicyConfig,
        highlight: false,
      },
      ...(isDev
        ? [
            {
              text: 'Report As False-Positive',
              icon: FaFlag,
              onClick: () => {},
              highlight: false,
            },
          ]
        : []),
    ],
  ];
};

export default useIssuesActions;
